// ===========================================
// WhatsApp Tab
// WhatsApp notification settings (Meta Cloud API - standalone integration)
// ===========================================

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  useIntegrationOverview,
  useWhatsAppStats,
} from '../../../hooks/useIntegrations';
import IntegrationKeyManager from './IntegrationKeyManager';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function WhatsAppTab() {
  const { data: overview, isLoading: overviewLoading } = useIntegrationOverview();
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useWhatsAppStats();

  const whatsappOverview = overview?.whatsapp;

  if (overviewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!whatsappOverview) {
    return <Alert severity="error">Failed to load WhatsApp settings.</Alert>;
  }

  const getStatusChip = (status: string) => {
    switch (status.toUpperCase()) {
      case 'DELIVERED':
        return <Chip label="Delivered" size="small" color="success" />;
      case 'SENT':
        return <Chip label="Sent" size="small" color="primary" />;
      case 'FAILED':
        return <Chip label="Failed" size="small" color="error" />;
      case 'PENDING':
        return <Chip label="Pending" size="small" color="warning" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Send WhatsApp notifications to parents for lesson reminders, payment confirmations,
        and other important updates via the Meta WhatsApp Cloud API.
      </Typography>

      {/* Per-School API Key Management */}
      <IntegrationKeyManager integration="whatsapp" />

      {/* Connection Status */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<WhatsAppIcon color={whatsappOverview.isConfigured ? 'success' : 'disabled'} />}
          title="WhatsApp Business API"
          subheader="Meta Cloud API"
        />
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {whatsappOverview.isConfigured ? (
                <>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="success.main">
                    Connected - WhatsApp Business API is configured
                  </Typography>
                </>
              ) : (
                <>
                  <CancelIcon color="error" fontSize="small" />
                  <Typography variant="body2" color="error.main">
                    Not configured - Add your Phone Number ID and Access Token above
                  </Typography>
                </>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Parents can opt-in to WhatsApp notifications from their notification preferences.
              If WhatsApp delivery fails, the system automatically falls back to SMS.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Card */}
      {whatsappOverview.isConfigured && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="WhatsApp Statistics"
            action={
              <Button
                startIcon={statsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => refetchStats()}
                disabled={statsLoading}
                size="small"
              >
                Refresh
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={3}>
              {/* Summary Stats */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Summary
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="h4">{whatsappOverview.messagesSentToday}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Messages Today
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{whatsappOverview.messagesSentThisMonth}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      This Month
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4">{whatsappOverview.deliverySuccessRate}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Delivery Rate
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Delivery by Type */}
              {stats?.deliveryByType && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Delivery by Notification Type
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Sent</TableCell>
                          <TableCell align="right">Delivered</TableCell>
                          <TableCell align="right">Failed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.deliveryByType.map((row) => (
                          <TableRow key={row.type}>
                            <TableCell>{row.type.replace(/_/g, ' ')}</TableCell>
                            <TableCell align="right">{row.sent}</TableCell>
                            <TableCell align="right">{row.delivered}</TableCell>
                            <TableCell align="right">
                              {row.failed > 0 ? (
                                <Chip label={row.failed} size="small" color="error" />
                              ) : (
                                0
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Divider />

              {/* Recent Messages */}
              {stats?.recentMessages && stats.recentMessages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Recent WhatsApp Messages
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Recipient</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Sent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.recentMessages.slice(0, 10).map((msg) => (
                          <TableRow key={msg.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {msg.recipientPhone
                                  ? msg.recipientPhone.slice(0, 4) + '****' + msg.recipientPhone.slice(-3)
                                  : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{msg.type.replace(/_/g, ' ')}</TableCell>
                            <TableCell>{getStatusChip(msg.status)}</TableCell>
                            <TableCell>
                              {new Date(msg.sentAt).toLocaleString('en-AU', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
